import React, { useState, useEffect, useMemo, useRef } from "react";
import { BellIcon, XMarkIcon, ExclamationTriangleIcon, CheckCircleIcon, InformationCircleIcon, XCircleIcon, ClockIcon } from "@heroicons/react/24/outline";
import { useNavigate, useLocation } from "react-router-dom";
import { useGlobal } from "../context/GlobalContext";
import {
  Notification,
  loadNotificationsFromCache,
  fetchNotificationsFromBackend,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  formatNotificationDateTime,
  shouldShowNotification,
  showBrowserNotification,
  requestNotificationPermission,
  sortNotificationsByPriority,
  convertBackendNotification,
  addNotification,
} from "../utils/notificationService";
import { notifyError } from "./notificationUtils";
import { getNotificationById } from "../Api/query/Notifications";
import {
  connectWebSocket,
  disconnectWebSocket,
  subscribeToNotifications,
  subscribeToConnection,
  NotificationData,
} from "../utils/websocketService";

const NotificationDropdown: React.FC = () => {
  const { notificationSidebarOpen, setNotificationSidebarOpen, notificationPreferences } = useGlobal();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const navigate = useNavigate();
  const location = useLocation();
  const isNavigatingRef = useRef(false);

  const [loading, setLoading] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [popupNotification, setPopupNotification] = useState<Notification | null>(null);
  const [popupVisible, setPopupVisible] = useState(false);
  const [bellButtonRef, setBellButtonRef] = useState<HTMLButtonElement | null>(null);
  const [progressWidth, setProgressWidth] = useState(100);
  const [showAllNotifications, setShowAllNotifications] = useState(false);

  // Load notifications function - only unread notifications
  const loadAndUpdateNotifications = async (useBackend = true) => {
    if (useBackend) {
      setLoading(true);
      try {
        // Fetch notifications based on showAllNotifications flag
        const result = await fetchNotificationsFromBackend(
          {}, // No status filter - we'll filter client-side to get only unread
          { page: 1, limit: 50 },
          !showAllNotifications // showOnlyUnread = true only if NOT showing all
        );
        // Time is already formatted in convertBackendNotification with proper date/time
        const updated = result.notifications;
        // Remove duplicates by notification_id or id
        const uniqueNotifications = updated.filter((n, index, self) => 
          index === self.findIndex((t) => 
            (n.notification_id && t.notification_id && n.notification_id === t.notification_id) ||
            n.id === t.id
          )
        );
        // Sort by priority (or just by timestamp if showing all)
        const sorted = sortNotificationsByPriority(uniqueNotifications);
        setNotifications(sorted);
      } catch (error) {
        console.error('Error loading notifications:', error);
        // Fallback to cache - filter based on showAllNotifications
        const loaded = loadNotificationsFromCache();
        const filtered = showAllNotifications ? loaded : loaded.filter(n => !n.read);
        // Remove duplicates
        const uniqueNotifications = filtered.filter((n, index, self) => 
          index === self.findIndex((t) => 
            (n.notification_id && t.notification_id && n.notification_id === t.notification_id) ||
            n.id === t.id
          )
        );
        // Update time for cached notifications (in case timestamp needs refresh)
        const updated = uniqueNotifications.map(n => ({
          ...n,
          time: formatNotificationDateTime(new Date(n.timestamp))
        }));
        const sorted = sortNotificationsByPriority(updated);
        setNotifications(sorted);
      } finally {
        setLoading(false);
      }
    } else {
      // Load from cache - filter based on showAllNotifications
      const loaded = loadNotificationsFromCache();
      const filtered = showAllNotifications ? loaded : loaded.filter(n => !n.read);
      // Remove duplicates
      const uniqueNotifications = filtered.filter((n, index, self) => 
        index === self.findIndex((t) => 
          (n.notification_id && t.notification_id && n.notification_id === t.notification_id) ||
          n.id === t.id
        )
      );
      // Update time for cached notifications (in case timestamp needs refresh)
      const updated = uniqueNotifications.map(n => ({
        ...n,
        time: formatNotificationDateTime(new Date(n.timestamp))
      }));
      const sorted = sortNotificationsByPriority(updated);
      setNotifications(sorted);
    }
  };

  // Initialize WebSocket connection and fetch notifications on mount
  useEffect(() => {
    // Connect WebSocket
    connectWebSocket();

    // Subscribe to WebSocket notifications
    const unsubscribeNotifications = subscribeToNotifications(async (notificationData: NotificationData) => {
      console.log('📬 Real-time notification received - FULL DATA:', notificationData);
      console.log('📬 Raw notification data - title:', notificationData.title);
      console.log('📬 Raw notification data - message:', notificationData.message);
      console.log('📬 Raw notification data - event_type:', notificationData.event_type);
      console.log('📬 Raw notification data - event_subtype:', notificationData.event_subtype);
      console.log('📬 Raw notification data - category:', notificationData.category);
      console.log('📬 Raw notification data - severity:', notificationData.severity);
      console.log('📬 Raw notification data - priority:', notificationData.priority);
      console.log('📬 Raw notification data - notification_id:', notificationData.notification_id);
      console.log('📬 Raw notification data - created_date:', notificationData.created_date);
      console.log('📬 Raw notification data - action_url:', notificationData.action_url);
      
      // CRITICAL: Fetch full notification details from backend API to get complete data
      // WebSocket might send incomplete data, so fetch from API to get the same data as sidebar
      let fullNotification: Notification | null = null;
      
      try {
        // Wait a bit for backend to process the notification
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Try to fetch the specific notification by ID first (most reliable)
        try {
          const backendNotif = await getNotificationById(notificationData.notification_id);
          if (backendNotif && backendNotif.title && backendNotif.message) {
            fullNotification = convertBackendNotification(backendNotif);
            console.log('📬 ✅ Fetched full notification by ID from backend API:', fullNotification);
            console.log('📬 Full notification title:', fullNotification.title);
            console.log('📬 Full notification message:', fullNotification.message);
            console.log('📬 Full notification action_url:', fullNotification.action_url);
            console.log('📬 Full notification event_type:', fullNotification.event_type);
            console.log('📬 Full notification event_subtype:', fullNotification.event_subtype);
          }
        } catch (idError) {
          console.log('📬 Could not fetch by ID, trying list:', idError);
          
          // Fallback: Fetch notifications list and find the matching one
          const result = await fetchNotificationsFromBackend(
            {}, // No filters
            { page: 1, limit: 100 },
            false // Get all, not just unread
          );
          
          // Find the matching notification by notification_id
          const matchingNotification = result.notifications.find(
            n => n.notification_id === notificationData.notification_id
          );
          
          if (matchingNotification) {
            fullNotification = matchingNotification;
            console.log('📬 ✅ Found full notification from backend API list:', fullNotification);
            console.log('📬 Full notification title:', fullNotification.title);
            console.log('📬 Full notification message:', fullNotification.message);
            console.log('📬 Full notification action_url:', fullNotification.action_url);
          } else {
            console.log('📬 ⚠️ Notification not found in backend API list');
          }
        }
      } catch (error) {
        console.error('📬 ❌ Error fetching notification from backend:', error);
      }
      
      // Use full notification from API if available, otherwise convert WebSocket data
      let notification: Notification;
      
      if (fullNotification) {
        // Use the notification from backend API (same as sidebar)
        notification = fullNotification;
        console.log('📬 Using notification from backend API (same as sidebar)');
      } else {
        // Fallback: Convert WebSocket data
        const notificationTitle = notificationData.title 
          ? String(notificationData.title).trim() 
          : (notificationData.event_subtype 
              ? `${notificationData.event_subtype.replace(/_/g, ' ')} ${notificationData.event_type || 'Request'}`
              : (notificationData.event_type 
                  ? `${notificationData.event_type} Notification`
                  : 'Notification'));
        
        const notificationMessage = notificationData.message 
          ? String(notificationData.message).trim() 
          : (notificationData.event_subtype 
              ? `${notificationData.event_subtype.replace(/_/g, ' ')} for ${notificationData.event_type || 'request'}`
              : (notificationData.title 
                  ? notificationTitle
                  : 'New notification'));
        
        notification = convertBackendNotification({
        notification_id: notificationData.notification_id,
          event_type: notificationData.event_type || '',
        event_subtype: notificationData.event_subtype,
          category: notificationData.category || 'other',
          severity: notificationData.severity || 'Info',
          priority: notificationData.priority || 'medium',
        recipient_id: '',
        recipient_email: '',
          title: notificationTitle,
          message: notificationMessage,
        action_url: notificationData.action_url,
        icon: notificationData.icon,
        status: 'New',
          requires_action: notificationData.requires_action || false,
          created_date: notificationData.created_date || new Date().toISOString(),
      });
      
      // Ensure time is formatted
      if (!notification.time) {
        notification.time = formatNotificationDateTime(new Date(notification.timestamp));
      }
      }
      
      console.log('📬 Final notification for popup:', notification);
      console.log('📬 Final notification - title:', notification.title);
      console.log('📬 Final notification - message:', notification.message);
      console.log('📬 Final notification - action_url:', notification.action_url);
      console.log('📬 Final notification - event_type:', notification.event_type);
      console.log('📬 Final notification - event_subtype:', notification.event_subtype);
      
      // Add to cache
      addNotification(notification);
      
      // Update state immediately for smooth real-time updates
      setNotifications(prev => {
        // Check if notification already exists by notification_id or id
        const exists = prev.some(n => 
          (n.notification_id && notification.notification_id && n.notification_id === notification.notification_id) ||
          (!n.notification_id && !notification.notification_id && n.id === notification.id)
        );
        
        if (!exists && !notification.read) {
          // Add new notification to the beginning
          const updated = [notification, ...prev];
          // Remove duplicates by notification_id first, then by id
          const unique = updated.filter((n, index, self) => {
            const firstIndex = self.findIndex((t) => {
              // Prioritize notification_id matching
              if (n.notification_id && t.notification_id) {
                return n.notification_id === t.notification_id;
              }
              // Fallback to id matching
              return n.id === t.id;
            });
            return index === firstIndex;
          });
          return sortNotificationsByPriority(unique);
        }
        return prev;
      });
      
      // Show browser notification if enabled
      if (notificationPreferences.pushNotifications) {
        showBrowserNotification(notification, notificationPreferences);
      }
      
      // CRITICAL: Wait a bit and check if we have complete notification data from sidebar state
      // The sidebar state might have the complete data after refresh
      setTimeout(() => {
        setNotifications(currentNotifications => {
          const completeNotification = currentNotifications.find(
            n => n.notification_id === notification.notification_id
          );
          
          if (completeNotification) {
            const hasRealData = 
              completeNotification.title && 
              completeNotification.title !== 'Notification' &&
              completeNotification.title.trim() !== '' &&
              completeNotification.message && 
              completeNotification.message !== 'New notification' &&
              completeNotification.message.trim() !== '';
            
            if (hasRealData) {
              console.log('📬 ✅ Found complete notification in state, updating for popup:', completeNotification);
              // Update notification to use complete data
              Object.assign(notification, completeNotification);
            }
          }
          
          return currentNotifications;
        });
      }, 800);
      
      // Show popup notification for new unread notifications
      // Check if notification should be shown (read status and preferences)
      const shouldShow = !notification.read && shouldShowNotification(notification, notificationPreferences);
      console.log('📬 Should show popup?', shouldShow, {
        read: notification.read,
        shouldShowNotification: shouldShowNotification(notification, notificationPreferences),
        title: notification.title,
        message: notification.message,
        action_url: notification.action_url,
        event_type: notification.event_type,
        event_subtype: notification.event_subtype,
      });
      
      if (shouldShow) {
        console.log('📬 Showing popup for notification:', notification);
        console.log('📬 Notification FULL data for popup:', {
          title: notification.title,
          message: notification.message,
          time: notification.time,
          timestamp: notification.timestamp,
          priority: notification.priority,
          event_subtype: notification.event_subtype,
          event_type: notification.event_type,
          action_url: notification.action_url,
          category: notification.category,
          severity: notification.severity,
          notification_id: notification.notification_id,
        });
        
        // Close any existing popup first, then show new one
        setPopupVisible(false);
        setProgressWidth(100);
        
        // Function to show popup with notification data
        const showPopupWithNotification = (notifToShow: Notification) => {
          // Ensure time is formatted
          const formattedTime = notifToShow.time || formatNotificationDateTime(new Date(notifToShow.timestamp));
          
          // Create a fresh notification object with ALL data - EXACT same format as sidebar
          const popupNotif: Notification = {
            ...notifToShow, // Use the notification as-is (from sidebar state if available)
            id: notifToShow.id || `notif_${Date.now()}`,
            notification_id: notifToShow.notification_id,
            title: notifToShow.title || 'Notification',
            message: notifToShow.message || '',
            time: formattedTime,
            timestamp: notifToShow.timestamp || Date.now(),
            read: notifToShow.read || false,
            type: notifToShow.type || 'info',
            category: notifToShow.category || 'other',
            priority: notifToShow.priority || 'medium',
            action_url: notifToShow.action_url,
            severity: notifToShow.severity,
            event_type: notifToShow.event_type,
            event_subtype: notifToShow.event_subtype,
          };
          
          console.log('📬 Popup notification object created with ALL data:', {
            title: popupNotif.title,
            message: popupNotif.message,
            action_url: popupNotif.action_url,
            event_type: popupNotif.event_type,
            event_subtype: popupNotif.event_subtype,
            category: popupNotif.category,
            priority: popupNotif.priority,
            notification_id: popupNotif.notification_id,
            fullObject: JSON.stringify(popupNotif, null, 2),
          });
          
          // Set notification first
          setPopupNotification(popupNotif);
          
          // Force a small delay before showing to ensure state is set
          setTimeout(() => {
            setPopupVisible(true);
            console.log('📬 Popup visibility set to true');
            console.log('📬 Popup should now be visible with notification:', popupNotif);
          }, 50);
        };
        
        // Wait for sidebar state to update, then use notification from there
        // Check multiple times to catch when the data is available
        let attempts = 0;
        const maxAttempts = 15; // Check for 3 seconds (15 * 200ms)
        const checkInterval = setInterval(() => {
          attempts++;
          
          // Get current notifications state
          setNotifications(currentNotifications => {
            // Find the notification in sidebar state
            const sidebarNotification = currentNotifications.find(
              n => n.notification_id === notification.notification_id
            );
            
            // Use sidebar notification if it has real data (not generic)
            if (sidebarNotification) {
              const hasRealData = 
                sidebarNotification.title && 
                sidebarNotification.title !== 'Notification' &&
                sidebarNotification.title.trim() !== '' &&
                sidebarNotification.message && 
                sidebarNotification.message !== 'New notification' &&
                sidebarNotification.message.trim() !== '';
              
              if (hasRealData) {
                console.log('📬 ✅ Found complete notification in sidebar state (attempt ' + attempts + '):', sidebarNotification);
                clearInterval(checkInterval);
                showPopupWithNotification(sidebarNotification);
                return currentNotifications; // Don't modify state
              } else {
                console.log('📬 ⚠️ Sidebar notification still has generic data (attempt ' + attempts + ')');
              }
            } else {
              console.log('📬 ⚠️ Notification not found in sidebar state yet (attempt ' + attempts + ')');
            }
            
            // If max attempts reached, use the notification we have
            if (attempts >= maxAttempts) {
              console.log('📬 ⚠️ Max attempts reached, using available notification:', notification);
              clearInterval(checkInterval);
              showPopupWithNotification(notification);
            }
            
            return currentNotifications; // Don't modify state
          });
        }, 200); // Check every 200ms
      } else {
        console.log('📬 Popup NOT shown because:', {
          read: notification.read,
          shouldShowNotification: shouldShowNotification(notification, notificationPreferences),
          title: notification.title,
          message: notification.message,
        });
      }
    });

    // Subscribe to connection status
    const unsubscribeConnection = subscribeToConnection((connected: boolean) => {
      setWsConnected(connected);
    });

    // Fetch initial notifications from backend
    loadAndUpdateNotifications(true);

    // Cleanup
    return () => {
      unsubscribeNotifications();
      unsubscribeConnection();
    };
  }, []);

  // Refresh notifications when sidebar opens
  useEffect(() => {
    if (notificationSidebarOpen) {
      loadAndUpdateNotifications(true);
    }
  }, [notificationSidebarOpen, showAllNotifications]);

  // Listen for new notifications (custom events)
  useEffect(() => {
    const handleNotificationAdded = () => {
      loadAndUpdateNotifications(false); // Refresh from cache
    };

    window.addEventListener('notificationAdded', handleNotificationAdded);
    return () => {
      window.removeEventListener('notificationAdded', handleNotificationAdded);
    };
  }, []);

  // Request notification permission on mount
  useEffect(() => {
    if (notificationPreferences.pushNotifications) {
      requestNotificationPermission();
    }
  }, [notificationPreferences.pushNotifications]);

  // Debug: Log when popupNotification changes
  useEffect(() => {
    if (popupNotification) {
      console.log('📬 Popup notification state updated:', {
        title: popupNotification.title,
        message: popupNotification.message,
        time: popupNotification.time,
        visible: popupVisible,
      });
    }
  }, [popupNotification, popupVisible]);

  // Auto-close popup after 7 seconds and animate progress bar
  useEffect(() => {
    if (popupVisible && popupNotification) {
      // Reset progress
      setProgressWidth(100);
      
      // Animate progress bar
      const progressInterval = setInterval(() => {
        setProgressWidth((prev) => {
          const newWidth = prev - (100 / 70); // Decrease by ~1.43% every 100ms (7 seconds total)
          return Math.max(0, newWidth);
        });
      }, 100);

      // Close popup after 7 seconds
      const timer = setTimeout(() => {
        setPopupVisible(false);
        clearInterval(progressInterval);
        // Clear notification after exit animation completes
        setTimeout(() => {
          setPopupNotification(null);
          setProgressWidth(100);
        }, 300); // Match exit animation duration
      }, 7000);

      return () => {
        clearTimeout(timer);
        clearInterval(progressInterval);
      };
    }
  }, [popupVisible, popupNotification]);

  // Handle popup close
  const handlePopupClose = () => {
    setPopupVisible(false);
    setProgressWidth(100);
    // Wait for exit animation to complete before clearing
    setTimeout(() => {
      setPopupNotification(null);
    }, 300); // Match animation duration
  };

  // Safe navigation helper - prevents duplicate navigation and handles errors gracefully
  const safeNavigate = (targetPath: string) => {
    // Prevent multiple simultaneous navigation attempts
    if (isNavigatingRef.current) {
      console.log('📬 ⚠️ Navigation already in progress, skipping');
      return;
    }

    try {
      // Normalize the path
      const normalizedPath = targetPath.trim();
      
      // Validate path
      if (!normalizedPath || !normalizedPath.startsWith('/')) {
        console.error('📬 ❌ Invalid navigation path:', normalizedPath);
        targetPath = '/access-request-queue';
      }

      // Check if we're already on the target route (prevents NavigationDuplicated errors)
      const currentPath = location.pathname;
      if (currentPath === normalizedPath) {
        console.log('📬 ℹ️ Already on target route:', normalizedPath);
        return;
      }

      // Set navigating flag
      isNavigatingRef.current = true;

      // Use requestAnimationFrame to ensure navigation happens after state updates
      requestAnimationFrame(() => {
        try {
          console.log('📬 🚀 Navigating to:', normalizedPath);
          navigate(normalizedPath, { replace: false });
        } catch (navError: any) {
          // Handle specific navigation errors
          if (navError?.name === 'NavigationDuplicated') {
            console.log('📬 ℹ️ NavigationDuplicated (already on route), ignoring');
          } else if (navError?.name === 'ChunkLoadError') {
            console.warn('📬 ⚠️ ChunkLoadError, retrying with page reload');
            // Retry with page reload for chunk errors
            setTimeout(() => {
              window.location.href = normalizedPath;
            }, 100);
          } else {
            console.error('📬 ❌ Navigation error:', navError);
            // Fallback navigation
            try {
              navigate('/access-request-queue', { replace: false });
            } catch (fallbackError) {
              console.error('📬 ❌ Fallback navigation failed, using window.location');
              window.location.href = '/access-request-queue';
            }
          }
        } finally {
          // Reset navigating flag after a delay
          setTimeout(() => {
            isNavigatingRef.current = false;
          }, 500);
        }
      });
    } catch (error: any) {
      console.error('📬 ❌ Error in safeNavigate:', error);
      isNavigatingRef.current = false;
      // Last resort fallback
      try {
        navigate('/access-request-queue', { replace: false });
      } catch {
        window.location.href = '/access-request-queue';
      }
    }
  };

  // Extract navigation path from notification
  const extractNavigationPath = (notification: Notification): string => {
    let navigationPath: string | null = null;
    
    try {
      // Priority 1: Use action_url if available and valid
      if (notification.action_url) {
        const actionUrl = String(notification.action_url).trim();
        if (actionUrl && actionUrl !== 'undefined' && actionUrl !== 'null' && actionUrl !== '') {
          // Ensure it starts with / if it's a relative path
          if (actionUrl.startsWith('/')) {
            navigationPath = actionUrl;
            console.log('📬 ✅ Navigating via action_url:', navigationPath);
          } else if (actionUrl.startsWith('http://') || actionUrl.startsWith('https://')) {
            // External URL - extract path and navigate in same window
            try {
              const url = new URL(actionUrl);
              // Extract the pathname from the URL
              navigationPath = url.pathname;
              console.log('📬 ✅ Extracted path from external URL, navigating to:', navigationPath);
            } catch (e) {
              console.error('📬 ❌ Error parsing external URL:', e);
              // Fall through to next priority
            }
          } else {
            // Relative path without leading slash
            navigationPath = '/' + actionUrl;
            console.log('📬 ✅ Navigating via action_url (added leading slash):', navigationPath);
          }
        }
      }
      
      // Priority 2: Extract request ID from message or title
      if (!navigationPath) {
        const requestId = extractRequestId(notification.message || '') || 
                         extractRequestId(notification.title || '');
        
        if (requestId) {
          // Validate request ID is numeric and reasonable
          if (/^\d+$/.test(requestId) && requestId.length >= 1 && requestId.length <= 10) {
            navigationPath = `/requests/${requestId}`;
            console.log('📬 ✅ Extracted request ID, navigating to:', navigationPath);
          } else {
            console.warn('📬 ⚠️ Invalid request ID format:', requestId);
          }
        }
      }
      
      // Priority 3: Try to extract from notification_id if it's a request ID
      if (!navigationPath && notification.notification_id) {
        const notifId = String(notification.notification_id);
        // Check if notification_id looks like a request ID (numeric)
        if (/^\d+$/.test(notifId) && notifId.length >= 1 && notifId.length <= 10) {
          navigationPath = `/requests/${notifId}`;
          console.log('📬 ✅ Using notification_id as request ID, navigating to:', navigationPath);
        }
      }
      
      // Priority 4: Category-based navigation
      if (!navigationPath) {
        const category = notification.category;
        if (category === 'request_approval' || category === 'request_execution' || category === 'request_status') {
          navigationPath = '/access-request-queue';
          console.log('📬 ✅ Using category-based navigation to request queue');
        } else {
          navigationPath = getNavigationPath(notification);
          console.log('📬 ✅ Using category-based navigation:', navigationPath);
        }
      }
      
      // Final fallback
      if (!navigationPath) {
        navigationPath = '/access-request-queue';
        console.log('📬 ⚠️ Using fallback navigation to request queue');
      }
    } catch (error) {
      console.error('📬 ❌ Error extracting navigation path:', error);
      navigationPath = '/access-request-queue';
    }

    return navigationPath || '/access-request-queue';
  };

  // Handle popup click - navigate to notification
  const handlePopupClick = () => {
    if (!popupNotification) {
      console.warn('📬 Popup clicked but no notification available');
      return;
    }

    // Prevent multiple clicks
    if (isNavigatingRef.current) {
      console.warn('📬 ⚠️ Navigation already in progress, ignoring click');
      return;
    }

    console.log('📬 Popup clicked, navigating to:', popupNotification.action_url || 'request details');
    console.log('📬 Popup notification data:', {
      id: popupNotification.id,
      notification_id: popupNotification.notification_id,
      action_url: popupNotification.action_url,
      title: popupNotification.title,
      message: popupNotification.message,
      event_type: popupNotification.event_type,
      event_subtype: popupNotification.event_subtype,
    });
    
    // Close popup first
    handlePopupClose();
    
    // Mark as read (async, don't wait)
    if (!popupNotification.read) {
      markNotificationAsRead(popupNotification.id).catch(error => {
        console.error('Error marking notification as read:', error);
      });
    }
    
    // Extract navigation path and navigate
    setTimeout(() => {
      try {
        const navigationPath = extractNavigationPath(popupNotification);
        safeNavigate(navigationPath);
      } catch (error: any) {
        console.error('📬 ❌ Error in handlePopupClick:', error);
        // Silent error handling - just navigate to fallback
        safeNavigate('/access-request-queue');
      }
    }, 150); // Slightly longer delay to ensure popup closes
  };

  // Filter and sort notifications based on preferences, with deduplication
  const visibleNotifications = useMemo(() => {
    const filtered = notifications.filter(n => 
      shouldShowNotification(n, notificationPreferences)
    );
    
    // Remove duplicates by notification_id or id to ensure accurate count
    const uniqueNotifications = filtered.filter((n, index, self) => 
      index === self.findIndex((t) => 
        (n.notification_id && t.notification_id && n.notification_id === t.notification_id) ||
        (!n.notification_id && !t.notification_id && n.id === t.id)
      )
    );
    
    return sortNotificationsByPriority(uniqueNotifications);
  }, [notifications, notificationPreferences]);

  // Calculate unread count with memoization for smooth updates - only count unique unread notifications
  const unreadCount = useMemo(() => {
    const unread = visibleNotifications.filter(n => !n.read);
    // Double-check for duplicates in unread count
    const uniqueUnread = unread.filter((n, index, self) => 
      index === self.findIndex((t) => 
        (n.notification_id && t.notification_id && n.notification_id === t.notification_id) ||
        (!n.notification_id && !t.notification_id && n.id === t.id)
      )
    );
    return uniqueUnread.length;
  }, [visibleNotifications]);

  // Extract request ID from notification message or title
  const extractRequestId = (text: string | undefined | null): string | null => {
    if (!text) return null;
    
    // Try multiple patterns: #631, REQ-631, Request #631, etc.
    const patterns = [
      /#(\d+)/i,                    // #631
      /REQ-(\d+)/i,                  // REQ-631
      /request\s+#?(\d+)/i,          // Request #631 or Request 631
      /request\s+(\d+)/i,            // Request 631
      /request\s+id[:\s]+(\d+)/i,    // Request ID: 631
      /id[:\s]+(\d+)/i,              // ID: 631
      /(\d{3,})/i,                   // Any 3+ digit number (likely a request ID)
    ];
    
    for (const pattern of patterns) {
      const match = text.match(pattern);
      if (match && match[1]) {
        const requestId = match[1].trim();
        // Validate it's a reasonable request ID (3-6 digits typically)
        if (requestId.length >= 3 && requestId.length <= 6 && /^\d+$/.test(requestId)) {
          console.log('📬 ✅ Extracted request ID:', requestId, 'from text:', text);
          return requestId;
      }
    }
    }
    
    console.log('📬 ⚠️ Could not extract request ID from text:', text);
    return null;
  };

  // Remove all duplicate information from message that's already in the title
  const cleanMessage = (title: string | undefined, message: string | undefined): string => {
    // Handle undefined/null values
    if (!message) return '';
    if (!title) return message;
    
    let cleanedMessage = message;
    
    // Extract request number from title
    const titleRequestMatch = title.match(/#(\d+)|REQ-(\d+)|request\s+#?(\d+)/i);
    if (titleRequestMatch) {
      const requestNum = titleRequestMatch[1] || titleRequestMatch[2] || titleRequestMatch[3];
      if (requestNum) {
        // Remove request number patterns from message
        const patterns = [
          new RegExp(`Request\\s+#${requestNum}\\s*:?\\s*`, 'gi'),
          new RegExp(`REQ-${requestNum}\\s*:?\\s*`, 'gi'),
          new RegExp(`#${requestNum}\\s*:?\\s*`, 'gi'),
          new RegExp(`request\\s+#?${requestNum}\\s*:?\\s*`, 'gi'),
        ];
        patterns.forEach(pattern => {
          cleanedMessage = cleanedMessage.replace(pattern, '').trim();
        });
      }
    }

    // Extract action types from title and remove from message
    const actionTypes = ['CREATE_GROUP', 'ADD_GROUP', 'CHANGE_OWNER', 'ASSIGN_OWNER', 
                         'ADD_MEMBER', 'REMOVE_MEMBER', 'ASSIGN_SECURABLE_OWNER', 
                         'SERVICE_ACCOUNT_OWNER', 'CHANGE_ACCOUNT_OWNER', 'CHANGE_SVC_OWNER'];
    
    for (const actionType of actionTypes) {
      if (title.toUpperCase().includes(actionType)) {
        // Remove action type from message (with various formats)
        const actionPatterns = [
          new RegExp(`\\b${actionType}\\b`, 'gi'),
          new RegExp(`a\\s+${actionType.replace(/_/g, '\\s+')}\\s+request`, 'gi'),
          new RegExp(`${actionType.replace(/_/g, '\\s+')}\\s+request`, 'gi'),
        ];
        actionPatterns.forEach(pattern => {
          cleanedMessage = cleanedMessage.replace(pattern, '').trim();
        });
        break;
      }
    }

    // Extract group name from title (e.g., "for vikas_notificaton_testing" or "for group name 'xyz'")
    const titleGroupPatterns = [
      /for\s+group\s+name\s+["']?([^"']+)["']?/i,
      /for\s+([^\s]+)/i,
      /group\s+name\s+["']?([^"']+)["']?/i,
    ];
    
    let groupName = '';
    for (const pattern of titleGroupPatterns) {
      const match = title.match(pattern);
      if (match && match[1]) {
        groupName = match[1].trim();
        // Don't capture if it's part of "Request #" pattern
        if (!groupName.match(/^#?\d+$/)) {
          break;
        }
      }
    }

    if (groupName && !groupName.match(/^#?\d+$/)) {
      // Remove group name patterns from message
      const escapedGroupName = groupName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const groupPatterns = [
        new RegExp(`group\\s+["']?${escapedGroupName}["']?`, 'gi'),
        new RegExp(`for\\s+group\\s+["']?${escapedGroupName}["']?`, 'gi'),
        new RegExp(`group\\s+name\\s+["']?${escapedGroupName}["']?`, 'gi'),
        new RegExp(`["']${escapedGroupName}["']`, 'gi'),
      ];
      groupPatterns.forEach(pattern => {
        cleanedMessage = cleanedMessage.replace(pattern, '').trim();
      });
    }

    // Clean up extra spaces, colons, and punctuation
    cleanedMessage = cleanedMessage
      .replace(/^[:,\s]+/, '')
      .replace(/\s+/g, ' ')
      .replace(/\s*\.\s*\./g, '.')
      .trim();

    return cleanedMessage;
  };

  // Format message to show only unique information (not in title)
  const formatShortMessage = (message: string | undefined, title: string | undefined): string => {
    // Handle undefined/null values
    if (!message) return '';
    
    // Extract submitter name (e.g., "yashu mittal has submitted", "john doe submitted")
    const submitterPatterns = [
      /([A-Z][a-z]+(?:\s+[A-Z][a-z]+)+)\s+has\s+submitted/i,
      /([A-Z][a-z]+(?:\s+[A-Z][a-z]+)+)\s+submitted/i,
    ];
    
    let submitter = '';
    for (const pattern of submitterPatterns) {
      const match = message.match(pattern);
      if (match && match[1]) {
        submitter = match[1].trim();
        break;
      }
    }

    // Extract status
    let status = '';
    const lowerMessage = message.toLowerCase();
    if (lowerMessage.includes('approval required') || lowerMessage.includes('your approval')) {
      status = 'pending approval';
    } else if (lowerMessage.includes('submitted')) {
      status = 'submitted';
    } else if (lowerMessage.includes('pending')) {
      status = 'pending';
    } else if (lowerMessage.includes('approved')) {
      status = 'approved';
    } else if (lowerMessage.includes('executed')) {
      status = 'executed';
    }

    // Build short message with only unique info (not in title)
    const parts: string[] = [];
    
    if (submitter) {
      parts.push(`${submitter} submitted`);
    }

    if (status) {
      parts.push(status);
    }

    // If we have nothing, show minimal info
    if (parts.length === 0) {
      return 'submitted.';
    }

    return parts.join('. ') + '.';
  };

  // Navigate based on notification category
  const getNavigationPath = (notification: Notification): string => {
    switch (notification.category) {
      case 'request_approval':
      case 'request_execution':
      case 'request_status':
        return '/access-request-queue';
      case 'account_expiry':
        return '/access-management-dashboard';
      case 'job_failure':
        return '/access-management-dashboard';
      case 'security_alert':
        return '/security-groups';
      case 'system':
        return '/settings';
      default:
        return '/access-management-dashboard';
    }
  };

  const handleNotificationClick = async (notification: Notification) => {
    // Prevent multiple clicks
    if (isNavigatingRef.current) {
      console.warn('📬 ⚠️ Navigation already in progress, ignoring click');
      return;
    }

    console.log('📬 Sidebar notification clicked:', {
      id: notification.id,
      notification_id: notification.notification_id,
      action_url: notification.action_url,
      title: notification.title,
      message: notification.message,
      category: notification.category,
      event_type: notification.event_type,
      event_subtype: notification.event_subtype,
    });

    // Mark as read immediately in state for smooth UI update
    // If NOT showing all notifications (unread mode), remove it from list immediately
    // If showing all notifications, just mark it as read but keep it visible
    if (showAllNotifications) {
      // In "show all" mode: mark as read but keep visible
      setNotifications(prev => prev.map(n => 
        n.id === notification.id ? { ...n, read: true } : n
      ));
    } else {
      // In unread mode: remove the notification from the list
      setNotifications(prev => prev.filter(n => n.id !== notification.id));
    }

    // Close notification sidebar first for smooth transition
    setNotificationSidebarOpen(false);

    // Mark as read (async backend call - don't wait for it to complete)
    markNotificationAsRead(notification.id).catch(error => {
      console.error('Error marking notification as read:', error);
      // On error, restore the notification to the list for retry
      if (showAllNotifications) {
        setNotifications(prev => prev.map(n => 
          n.id === notification.id ? { ...n, read: false } : n
        ));
      } else {
        setNotifications(prev => [notification, ...prev]);
      }
    });

    // Small delay to allow sidebar close animation before navigation
    setTimeout(() => {
      try {
        const navigationPath = extractNavigationPath(notification);
        safeNavigate(navigationPath);
      } catch (error: any) {
        console.error('📬 ❌ Error in handleNotificationClick:', error);
        // Silent error handling - just navigate to fallback
        safeNavigate('/access-request-queue');
      }
    }, 200); // Small delay for smooth sidebar close animation
  };

  const markAllAsRead = async () => {
    try {
      // Update state immediately for smooth UI
      // If not showing all notifications, clear the list (all marked as read)
      // If showing all, update all to read status but keep them visible
      if (!showAllNotifications) {
        setNotifications([]);
      } else {
        setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      }
      
      // Mark all as read in backend
      await markAllNotificationsAsRead();
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      // Error is logged but we don't reload - keep the optimistic update
    }
  };

  const closeNotificationSidebar = () => {
    setNotificationSidebarOpen(false);
  };

  const dismissNotification = async (notification: Notification, e: React.MouseEvent) => {
    // Prevent triggering handleNotificationClick
    e.stopPropagation();

    // Mark as read immediately in state - always remove from display
    setNotifications(prev => prev.filter(n => n.id !== notification.id));

    // Mark as read on backend
    markNotificationAsRead(notification.id).catch(error => {
      console.error('Error marking notification as read:', error);
      // On error, restore the notification to the list
      setNotifications(prev => [notification, ...prev]);
    });
  };

  const getNotificationIcon = (type?: string) => {
    switch (type) {
      case 'success':
        return <CheckCircleIcon className="h-4 w-4" />;
      case 'warning':
        return <ExclamationTriangleIcon className="h-4 w-4" />;
      case 'error':
        return <XCircleIcon className="h-4 w-4" />;
      default:
        return <InformationCircleIcon className="h-4 w-4" />;
    }
  };

  const getNotificationColor = (type?: string) => {
    // Use consistent professional colors - mostly gray/blue tones
    switch (type) {
      case 'success':
        return 'bg-gray-50 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700';
      case 'warning':
        return 'bg-gray-50 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700';
      case 'error':
        return 'bg-gray-50 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700';
    }
  };

  const getPriorityBadge = (priority?: string) => {
    // Simplified priority badge with consistent gray styling
    switch (priority) {
      case 'high':
        return (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300">
            High
          </span>
        );
      case 'medium':
        return (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300">
            Medium
          </span>
        );
      case 'low':
        return (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300">
            Low
          </span>
        );
      default:
        return null;
    }
  };

  const getPriorityBorder = (priority?: string, read?: boolean) => {
    // Use consistent blue accent for unread notifications
    if (!read) {
      return 'border-l-2 border-l-blue-500';
    }
    return '';
  };

  return (
    <>
      {/* Notification Bell Button */}
      <button
        ref={setBellButtonRef}
        className="relative p-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-[#32196c] focus:ring-offset-2"
        onClick={() => setNotificationSidebarOpen(true)}
        aria-label="Notifications"
      >
        <BellIcon className="h-6 w-6" />
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs font-bold text-white transition-opacity duration-200">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Popup Notification - Show even when modals are open */}
      {popupNotification && (
        <div
          className={`fixed transition-all duration-300 ${
            popupVisible
              ? 'animate-slideInFromBell opacity-100 pointer-events-auto'
              : 'animate-slideOutToBell opacity-0 pointer-events-none'
          }`}
          style={{
            top: bellButtonRef ? `${bellButtonRef.getBoundingClientRect().bottom + 12}px` : '80px',
            right: bellButtonRef ? `${window.innerWidth - bellButtonRef.getBoundingClientRect().right}px` : '16px',
            maxWidth: window.innerWidth < 640 ? 'calc(100vw - 32px)' : '380px',
            minWidth: window.innerWidth < 640 ? 'auto' : '320px',
            zIndex: 99999, // Higher than modals (z-50) and sidebar (z-9999)
            transformOrigin: 'top right',
          }}
        >
          <div
            className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 shadow-2xl cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-all duration-200 overflow-hidden ring-1 ring-gray-200 dark:ring-gray-700 hover:shadow-xl relative"
            onClick={handlePopupClick}
          >
            {/* Arrow pointing to bell icon */}
            <div className="absolute -top-2 right-6 w-4 h-4 bg-white dark:bg-gray-900 border-l border-t border-gray-200 dark:border-gray-800 transform rotate-45" />
            <div className="p-2.5">
              <div className="flex items-start gap-2">
                {/* Icon */}
                <div className={`flex-shrink-0 w-7 h-7 rounded-md flex items-center justify-center border ${getNotificationColor(popupNotification.type)}`}>
                  {getNotificationIcon(popupNotification.type)}
                </div>
                
                {/* Content */}
                <div className="flex-1 min-w-0">
                  {/* Title and Priority - Same format as sidebar */}
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <p className={`text-xs font-semibold ${!popupNotification.read ? 'text-gray-900 dark:text-gray-100' : 'text-gray-700 dark:text-gray-300'} break-words`}>
                          {/* Use EXACT same logic as sidebar: notification.title || 'Notification' */}
                          {popupNotification.title || 'Notification'}
                        </p>
                        {popupNotification.priority && (
                          <span className="flex-shrink-0">
                            {getPriorityBadge(popupNotification.priority)}
                          </span>
                        )}
                        {!popupNotification.read && (
                          <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-[#32196c] dark:bg-[#32196c]"></span>
                        )}
                      </div>
                    </div>
                    {/* Close button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePopupClose();
                      }}
                      className="flex-shrink-0 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
                      aria-label="Close notification"
                    >
                      <XMarkIcon className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  
                  {/* Message - Use EXACT same logic as sidebar notification card */}
                  {(() => {
                    // Use EXACT same logic as sidebar (lines 1032-1039)
                    const title = popupNotification.title || 'Notification';
                    const message = popupNotification.message || '';
                    
                    // Remove request number from message if it's in the title (to avoid duplication)
                    // Use the SAME cleanMessage function as sidebar
                    const cleanedMessage = cleanMessage(title, message);
                    
                    // Show the cleaned message (which has duplicates removed but keeps all details)
                    // Use EXACT same logic as sidebar: cleanedMessage || message
                    const displayMessage = cleanedMessage || message;
                    
                    console.log('📬 Popup message rendering (same as sidebar):', { 
                      title, 
                      rawMessage: message,
                      cleanedMessage,
                      displayMessage,
                      fullNotification: popupNotification 
                    });
                    
                    // Always show message if it exists (same as sidebar)
                    if (displayMessage && displayMessage.trim().length > 0) {
                      return (
                        <p className="text-xs text-gray-600 dark:text-gray-400 mb-1.5 leading-relaxed break-words" style={{
                          display: '-webkit-box',
                          WebkitLineClamp: 4,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}>
                          {displayMessage}
                        </p>
                      );
                    }
                    
                    // If no message, don't show message area (same as sidebar behavior)
                    return null;
                  })()}
                  
                  {/* Time and Event Subtype - Same format as sidebar */}
                  <div className="flex items-center gap-1 text-[10px] text-gray-500 dark:text-gray-400">
                    <ClockIcon className="h-3 w-3" />
                    <span>{popupNotification.time || 'Just now'}</span>
                    {popupNotification.event_subtype && (
                      <>
                        <span className="mx-1">•</span>
                        <span className="text-gray-400 dark:text-gray-500 capitalize">
                          {popupNotification.event_subtype.toLowerCase().replace(/_/g, ' ')}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Progress Bar for Auto-close */}
            <div className="h-0.5 bg-gray-200 dark:bg-gray-700 relative overflow-hidden">
              <div
                className="h-full bg-gray-400 dark:bg-gray-500 transition-all duration-100 ease-linear"
                style={{
                  width: `${progressWidth}%`,
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Notification Sidebar */}
      {notificationSidebarOpen && (
        <div className="fixed top-0 left-0 right-0 bottom-0 flex justify-end z-[9999]" style={{ top: 0, left: 0, right: 0, bottom: 0 }}>
          {/* Overlay */}
          <div
            className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-40 backdrop-blur-sm animate-fadeIn"
            onClick={closeNotificationSidebar}
            style={{ top: 0, left: 0, right: 0, bottom: 0 }}
          />
          {/* Sidebar */}
          <div className="fixed top-0 right-0 bottom-0 w-full sm:w-96 bg-white dark:bg-gray-900 shadow-2xl flex flex-col transform transition-transform duration-300 ease-out animate-slideInRight" style={{ top: 0, height: '100vh' }}>
            {/* Sidebar Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
              <div className="flex items-center space-x-2">
                <BellIcon className="h-5 w-5 text-[#32196c] dark:text-[#32196c]" />
                <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Notifications</h2>
                {unreadCount > 0 && (
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs font-bold text-white transition-all duration-200">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
                {wsConnected && (
                  <span className="flex h-2 w-2 rounded-full bg-green-500" title="Real-time connected"></span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button
                    onClick={markAllAsRead}
                    className="text-sm text-[#32196c] dark:text-[#32196c] hover:text-[#4a2a8a] dark:hover:text-[#4a2a8a] font-medium"
                  >
                    Mark all as read
                  </button>
                )}
                <button
                  onClick={closeNotificationSidebar}
                  className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full p-1 transition-colors"
                  aria-label="Close panel"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Sidebar Body */}
            <div className="flex-1 overflow-y-auto p-6 bg-gray-50 dark:bg-gray-950">
              {loading ? (
                <div className="text-center py-16">
                  <div className="h-16 w-16 mx-auto mb-4 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                    <svg className="animate-spin h-8 w-8 text-[#32196c]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Loading notifications...</p>
                </div>
              ) : visibleNotifications.length === 0 ? (
                <div className="text-center py-16">
                  <div className="h-16 w-16 mx-auto mb-4 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                    <BellIcon className="h-8 w-8 text-gray-400 dark:text-gray-500" />
                  </div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">No notifications</p>
                  <p className="text-xs text-gray-500 dark:text-gray-500">You're all caught up!</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {visibleNotifications.map((notification) => {
                    // Safely get title and message with fallbacks
                    const title = notification.title || 'Notification';
                    const message = notification.message || '';
                    
                    // Remove request number from message if it's in the title (to avoid duplication)
                    const cleanedMessage = cleanMessage(title, message);
                    
                    // Show the cleaned message (which has duplicates removed but keeps all details)
                    const displayMessage = cleanedMessage || message;
                    
                    return (
                    <button
                      key={notification.id}
                      onClick={() => handleNotificationClick(notification)}
                        className={`w-full text-left bg-white dark:bg-gray-900 rounded-md border border-gray-200 dark:border-gray-800 p-2.5 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${
                        !notification.read 
                            ? 'bg-gray-50 dark:bg-gray-800'
                          : ''
                      } ${getPriorityBorder(notification.priority, notification.read)}`}
                    >
                        <div className="flex items-start gap-2">
                          <div className={`flex-shrink-0 w-7 h-7 rounded-md flex items-center justify-center border ${getNotificationColor(notification.type)}`}>
                          {getNotificationIcon(notification.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <p className={`text-xs font-semibold ${!notification.read ? 'text-gray-900 dark:text-gray-100' : 'text-gray-700 dark:text-gray-300'}`}>
                                    {title}
                                  </p>
                                  {notification.priority && (
                                    <span className="flex-shrink-0">
                                      {getPriorityBadge(notification.priority)}
                                    </span>
                                  )}
                            {!notification.read && (
                                    <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-[#32196c] dark:bg-[#32196c]"></span>
                            )}
                                </div>
                              </div>
                              <button
                                onClick={(e) => dismissNotification(notification, e)}
                                className="flex-shrink-0 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded p-1 transition-colors"
                                aria-label="Dismiss notification"
                              >
                                <XMarkIcon className="h-4 w-4" />
                              </button>
                                </div>
                            {displayMessage && (
                              <p className="text-xs text-gray-600 dark:text-gray-400 mb-1.5 leading-relaxed" style={{
                                display: '-webkit-box',
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden'
                              }}>
                                {displayMessage}
                              </p>
                            )}
                            <div className="flex items-center gap-1 text-[10px] text-gray-500 dark:text-gray-400">
                              <ClockIcon className="h-3 w-3" />
                              <span>{notification.time || 'Just now'}</span>
                              {notification.event_subtype && (
                                <>
                                  <span className="mx-1">•</span>
                                  <span className="text-gray-400 dark:text-gray-500 capitalize">
                                    {notification.event_subtype.toLowerCase().replace(/_/g, ' ')}
                                  </span>
                                </>
                              )}
                          </div>
                        </div>
                      </div>
                    </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Sidebar Footer */}
            {visibleNotifications.length > 0 && (
              <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
                <button
                  onClick={() => setShowAllNotifications(!showAllNotifications)}
                  className="w-full text-center text-sm text-[#32196c] dark:text-[#32196c] hover:text-[#4a2a8a] dark:hover:text-[#4a2a8a] font-medium"
                >
                  {showAllNotifications ? 'View unread notifications' : 'View all notifications'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default NotificationDropdown;

